import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";

import { default_linechart_data } from "../../constants/charts";
import { getLineChartData } from "../../utils/analyticsUtils";
import { useWindowSize } from "../../hooks";
import {
  CHART_PRIMARY_COLOR,
  lineChartBaseOptions,
  lineDatasetStyle,
} from "../../utils/chartTheme";

// Grades are always scored out of 10, so their axis is pinned rather than fitted to
// the data — otherwise a 7.2 → 7.8 run reads like a cliff.
const GRADE_AXIS_MAX = 10;
const isGradeChart = (type) => type === "calls";

const options = (
  title,
  min_value,
  max_value,
  type,
  strictMaxValue = -1,
  minMaxAxisDiffFactor = 0
) => {
  const is_grade_chart = isGradeChart(type);

  const getLabelCallback = () => {
    if (type === "calls") {
      return (context) => `${context.parsed.y}`;
    }

    if (type === "sales_companies" || type === "operator_sales") {
      return (context) =>
        `${context.parsed.y.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
    }
    return () => "";
  };

  return lineChartBaseOptions({
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: () => "",
          label: getLabelCallback(),
        },
        displayColors: false,
      },
    },
    scales: {
      y: {
        display: true,
        max:
          strictMaxValue !== -1
            ? strictMaxValue
            : is_grade_chart
            ? GRADE_AXIS_MAX
            : Math.ceil(max_value * (1 + minMaxAxisDiffFactor)),
        min: is_grade_chart
          ? 0
          : Math.floor(
              min_value * (1 - minMaxAxisDiffFactor) > 0
                ? min_value * (1 - minMaxAxisDiffFactor)
                : 0
            ),
        beginAtZero: true,
        ...(is_grade_chart ? { ticks: { stepSize: 2 } } : {}),
      },
      x: {
        beginAtZero: true,
      },
    },
    layout: {
      padding: { left: 10, right: 10, top: 10, bottom: 10 },
    },
  });
};

// Datasets come from getLineChartData / callers, which only set colours and data.
// Reference-style dashed threshold lines keep their flat look — no fill, no dots.
const themeDatasets = (chart_data) => {
  if (!chart_data?.datasets) return chart_data;

  return {
    ...chart_data,
    datasets: chart_data.datasets.filter(Boolean).map((dataset) => {
      if (dataset.borderDash) {
        return { ...dataset, fill: false, pointRadius: 0, pointHoverRadius: 0 };
      }
      const color =
        typeof dataset.borderColor === "string" ? dataset.borderColor : CHART_PRIMARY_COLOR;
      return { ...dataset, ...lineDatasetStyle(color) };
    }),
  };
};

const LineChart = ({
  data,
  type = "line",
  title,
  limit = 0,
  strictMaxChartAxisValue = -1,
  minMaxAxisDiffFactor = 0,
  // Floor rather than a fixed height: the chart still grows to fill a taller
  // parent, but never collapses to the canvas default when the parent is auto.
  minHeight = 320,
}) => {
  const [linechartData, setLinechartData] = useState(default_linechart_data);
  const [_width, _height] = useWindowSize();

  useEffect(() => {
    const line_chart_data = getLineChartData(type, data, limit);
    if (line_chart_data) setLinechartData(line_chart_data);
  }, [data, type, limit]);

  const min_value = Math.min(...linechartData.datasets[0].data.slice());
  const max_value = Math.max(...linechartData.datasets[0].data.slice());

  const chartOptions = options(
    title,
    min_value,
    max_value,
    type,
    strictMaxChartAxisValue,
    minMaxAxisDiffFactor
  );

  return (
    <div style={{ position: "relative", height: "100%", minHeight }}>
      <Line
        type="line"
        data={themeDatasets(linechartData)}
        options={chartOptions}
      />
    </div>
  );
};

export default LineChart;
